import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";
import PersonalAINotFound from "sections/PersonalAI/PersonalAINotFound";
import Loader from "components/Loader";
import PrimaryButton from "components/common/Button/PrimaryButton";
import AcademicCap from "assets/icons/AcademicCap";
import Popularity from "assets/icons/PopularityIcon";
import Clock from "assets/icons/Clock";
import HollandCodeAttempt from "./HollandCodeAttempt";
import { useState } from "react";
import HeaderSection from "components/HeaderSection";
import { useTranslation } from "react-i18next";
import { useDemoQuestionnaire } from "api/questionnaire";
import { ApplyBlock } from "components/ApplyNow/ApplyBlock";
import invitationForSimulator from "assets/images/persona-ai/quiz-link-to-simulator.png";
import { AutoTextSize } from "auto-text-size";

export default function HollandCodeQuiz() {
  const { id } = useParams();
  const { dir, language } = useAppSelector(getSettings);
  const [startQuiz, setStartQuiz] = useState(false);
  const { t } = useTranslation();
  const { data, isLoading } = useDemoQuestionnaire(id);
  const [result, setResult] = useState(
    JSON.parse(localStorage.getItem("quiz") ?? "{}")
  );
  const navigate = useNavigate();

  const finishResult = (res) => {
    if (!res) return;
    setStartQuiz(false);
    setResult(res);
    localStorage.setItem("quiz", JSON.stringify(res));
  };

  if (startQuiz) return <HollandCodeAttempt onFinish={finishResult} />;

  if (isLoading) return <Loader type="block" open />;
  if (!data) return <PersonalAINotFound />;
  return (
    <div dir={dir} className="bg-white">
      <HeaderSection bgImage={data?.cover_image} onBack="/register">
        <div className="">
          <h1 className="text-white font-bold text-4xl">
            {t("freeQuizTitle")}
          </h1>
        </div>
        <div></div>
      </HeaderSection>
      <div className="container flex flex-col md:flex-row justify-between gap-10 flex-1 mt-5 md:mt-28 px-10 pb-10 md:pb-28">
        <div className="flex flex-col w-full md:w-2/3">
          <div className="flex md:flex-row gap-y-3 md:gap-x-3 justify-between items-center flex-col">
            <div className="flex flex-col items-center md:items-start gap-3">
              <h1 className="text-theme-blue text-2xl font-semibold">
                {data?.title[language]}
              </h1>
              <div className="flex items-center gap-3 justify-between w-full md:justify-normal">
                <div className="flex items-center gap-3">
                  <Clock />
                  <span>
                    {data?.duration} {t("quizTimeMinutes")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <AcademicCap />
                  <span>{t(data?.difficulty)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Popularity />
                  <span>{data?.popularity}%</span>
                </div>
              </div>
            </div>
            <div className="block md:hidden w-full">
              <WalkthroughPersonalAICTA
                result={result}
                descriptionImage={data?.description_image}
              />
            </div>
            {result?.progress_precentage !== 100 ? (
              <div className="mt-6 md:mt-0">
                <PrimaryButton
                  className="!bg-theme-yellow px-10 py-2 rounded-lg"
                  onClick={() => setStartQuiz(true)}
                >
                  {t("quizStart")}
                </PrimaryButton>
              </div>
            ) : (
              ""
            )}
          </div>
          {result?.progress_precentage !== 100 ? (
            <pre className="font-heebo whitespace-break-spaces text-justify md:text-start my-5">
              {data?.description[language]}
            </pre>
          ) : (
            <div className="my-5">
              <p className="text-justify md:text-start">
                {t("hollandBasedOnResultsTitle")}
              </p>
              <div className="my-5">
                {result?.result?.map((res, index) => {
                  if (index > 2) return null;
                  return (
                    <div className="mt-3" key={index}>
                      <h2 className="font-semibold">
                        {/* {res.group[0]} -  */}
                        {t(`holland${res.group}`)}
                      </h2>
                      <ul>
                        {res.explanation[language]
                          .split(".")
                          .filter((item) => item)
                          .map((item, index) => (
                            <li
                              key={"point-explain" + index}
                              className="list-disc ltr:ml-6 rtl:mr-6 text-justify md:text-start"
                            >
                              {item}.
                            </li>
                          ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
              <p className="text-justify md:text-start">
                {t("hollandFinalParagraph")}
              </p>
            </div>
          )}
        </div>
        <div className="hidden md:block">
          <WalkthroughPersonalAICTA
            result={result}
            descriptionImage={data?.description_image}
          />
        </div>
      </div>
    </div>
  );
}

const WalkthroughPersonalAICTA = ({ result, descriptionImage }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <>
      {result?.progress_precentage === 100 ? (
        <div className="bg-theme-blue relative flex justify-center">
          <div
            className="triangle rotate-180 absolute -top-[1px]"
            style={{
              borderWidth: "0 1rem 1rem 1rem",
              borderColor: "transparent transparent white transparent",
            }}
          />
          <ApplyBlock
            text={
              <AutoTextSize
                minFontSizePx={10}
                maxFontSizePx={18}
                className="max-w-[230px] text-center"
              >
                {t("suggestRegisterSimulatorTitle")}
              </AutoTextSize>
            }
            buttonText={t("goToSimulatorButton")}
            onClick={() => {
              navigate("/register");
            }}
            buttonClass="max-w-[230px] w-full"
          />
        </div>
      ) : (
        ""
      )}
      {result?.progress_precentage === 100 ? (
        <img
          src={invitationForSimulator}
          className="md:max-h-96 md:h-full self-center object-contain mt-4 mx-auto"
        />
      ) : (
        <img
          src={descriptionImage}
          className="md:max-h-96 md:h-full self-center object-contain"
        />
      )}
    </>
  );
};
